import React, { useEffect, useState, useCallback } from 'react';
import { TextField, Button, Typography, Box, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, FormControlLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../components/AuthContext.tsx';
import { authenticatedApiCall } from '../api.ts';

interface ParkingSpot {
  id: number;
  spot_number: string;
  has_electric_charger: boolean;
  is_pmr_accessible: boolean;
}

function ParkingSpotManagement() {
  const { token, isAdmin } = useAuth();
  const [spots, setSpots] = useState<ParkingSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newSpotNumber, setNewSpotNumber] = useState('');
  const [newSpotElectric, setNewSpotElectric] = useState(false);
  const [newSpotPMR, setNewSpotPMR] = useState(false);
  const [addSpotError, setAddSpotError] = useState<string | null>(null);

  // State for editing
  const [editingSpot, setEditingSpot] = useState<ParkingSpot | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchParkingSpots = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authenticatedApiCall('GET', '/parking_spots/', token);
      setSpots(data);
    } catch (err: any) {
      setError(err.message || 'Échec de la récupération des places de parking');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAdmin && token) {
      fetchParkingSpots();
    } else if (!isAdmin) {
      setError("Vous n'avez pas les droits d'administration pour voir cette page.");
      setLoading(false);
    }
  }, [token, isAdmin, fetchParkingSpots]);

  const handleAddParkingSpot = async (event: React.FormEvent) => {
    event.preventDefault();
    setAddSpotError(null);
    try {
      await authenticatedApiCall('POST', '/parking_spots/', token, {
        spot_number: newSpotNumber,
        has_electric_charger: newSpotElectric,
        is_pmr_accessible: newSpotPMR,
      });
      setNewSpotNumber('');
      setNewSpotElectric(false);
      setNewSpotPMR(false);
      fetchParkingSpots(); // Refresh the list
    } catch (err: any) {
      setAddSpotError(err.message || "Échec de l'ajout de la place de parking");
    }
  };

  const handleDeleteSpot = async (spotId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette place de parking ?')) {
      try {
        await authenticatedApiCall('DELETE', `/parking_spots/${spotId}`, token);
        fetchParkingSpots(); // Refresh the list
      } catch (err: any) {
        setError(err.message || 'Échec de la suppression de la place');
      }
    }
  };

  const handleOpenEditModal = (spot: ParkingSpot) => {
    setEditingSpot(spot);
    setIsModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditingSpot(null);
    setIsModalOpen(false);
  };

  const handleUpdateSpot = async () => {
    if (!editingSpot) return;
    try {
      await authenticatedApiCall('PUT', `/parking_spots/${editingSpot.id}`, token, {
        spot_number: editingSpot.spot_number,
        has_electric_charger: editingSpot.has_electric_charger,
        is_pmr_accessible: editingSpot.is_pmr_accessible,
      });
      handleCloseEditModal();
      fetchParkingSpots();
    } catch (err: any) {
      // You could show an error inside the modal
      console.error("Failed to update spot", err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Gestion des Places de Parking
      </Typography>

      {isAdmin && (
        <Box component="form" onSubmit={handleAddParkingSpot} sx={{ mt: 3, mb: 4, p: 3, border: '1px solid #ccc', borderRadius: '8px' }}>
          <Typography variant="h5" gutterBottom>Ajouter une nouvelle place</Typography>
          {addSpotError && <Alert severity="error" sx={{ mb: 2 }}>{addSpotError}</Alert>}
          <TextField
            margin="normal"
            required
            fullWidth
            id="spot_number"
            label="Numéro de la place (ex: 107)"
            name="spot_number"
            value={newSpotNumber}
            onChange={(e) => setNewSpotNumber(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Checkbox checked={newSpotElectric} onChange={(e) => setNewSpotElectric(e.target.checked)} />}
            label="Dispose d'une borne électrique"
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={<Checkbox checked={newSpotPMR} onChange={(e) => setNewSpotPMR(e.target.checked)} />}
            label="Accessible PMR"
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
          >
            Ajouter la place
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="parking spots table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Numéro de place</TableCell>
              <TableCell>Borne électrique</TableCell>
              <TableCell>Accessible PMR</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {spots.map((spot) => (
              <TableRow key={spot.id}>
                <TableCell>{spot.id}</TableCell>
                <TableCell>{spot.spot_number}</TableCell>
                <TableCell>{spot.has_electric_charger ? 'Oui' : 'Non'}</TableCell>
                <TableCell>{spot.is_pmr_accessible ? 'Oui' : 'Non'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenEditModal(spot)}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDeleteSpot(spot.id)} color="error"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseEditModal}>
        <DialogTitle>Modifier la place de parking</DialogTitle>
        <DialogContent>
          {editingSpot && (
            <Box component="form" sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Numéro de la place"
                value={editingSpot.spot_number}
                onChange={(e) => setEditingSpot({ ...editingSpot, spot_number: e.target.value })}
              />
              <FormControlLabel
                control={<Checkbox checked={editingSpot.has_electric_charger} onChange={(e) => setEditingSpot({ ...editingSpot, has_electric_charger: e.target.checked })} />}
                label="Dispose d'une borne électrique"
              />
              <FormControlLabel
                control={<Checkbox checked={editingSpot.is_pmr_accessible} onChange={(e) => setEditingSpot({ ...editingSpot, is_pmr_accessible: e.target.checked })} />}
                label="Accessible PMR"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>Annuler</Button>
          <Button onClick={handleUpdateSpot} variant="contained">Sauvegarder</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ParkingSpotManagement;